import React, { useState } from 'react';
import DOMPurify from 'dompurify';
import { ChatBotResponseType } from '@/domain/ChatBot/ChatBotTypes';

interface ChatMessageRendererProps {
  responseType: ChatBotResponseType;
  content: string;
  columns?: string[];
  rows?: string[][];
  url?: string;
}

const ChatMessageRenderer: React.FC<ChatMessageRendererProps> = ({ responseType, content, columns, rows, url }) => {
  const [imageError, setImageError] = useState(false);

  console.log('🎨 [ChatMessageRenderer] Rendering:', {
    responseType,
    contentLength: content?.length,
    contentPreview: content?.substring(0, 100),
    hasColumns: !!columns,
    hasRows: !!rows,
  });

  switch (responseType) {
    case 'table':
      // Renderizar el contenido HTML primero (mensaje explicativo)
      const htmlContent = content ? (
        <div
          style={{ fontSize: '13px', lineHeight: '1.5', marginBottom: '12px' }}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }}
        />
      ) : null;

      // Renderizar la tabla si hay columns y rows
      const tableContent = columns && rows && columns.length > 0 && rows.length > 0 ? (
        <div style={{ overflowX: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '12px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f5f5f5' }}>
                {columns.map((column, index) => (
                  <th
                    key={index}
                    style={{
                      border: '1px solid #e0e0e0',
                      padding: '8px 12px',
                      textAlign: 'center',
                      fontWeight: '600',
                      color: '#333',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <td
                      key={cellIndex}
                      style={{
                        border: '1px solid #e0e0e0',
                        background: '#fff',
                        padding: '6px 12px',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null;

      return (
        <div>
          {htmlContent}
          {tableContent}
        </div>
      );

    case 'html':
      return (
        <div
          style={{ fontSize: '13px', lineHeight: '1.5', overflowX: 'auto' }}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }}
        />
      );

    case 'svg':
      console.log('🖼️ [ChatMessageRenderer] SVG case:', {
        startsWithSvgTag: content?.trim().startsWith('<svg'),
        contentLength: content?.length,
        firstChars: content?.substring(0, 50),
      });
      if (content.trim().startsWith('<svg')) {
        let svgContent = content.replace(/\\"/g, '"').replace(/\\'/g, "'");
        if (!/\bwidth=/.test(svgContent)) {
          const viewBoxMatch = svgContent.match(/viewBox=["']\s*[\d.]+\s+[\d.]+\s+([\d.]+)\s+([\d.]+)\s*["']/);
          const svgWidth = viewBoxMatch?.[1] ?? '800';
          const svgHeight = viewBoxMatch?.[2] ?? '400';
          svgContent = svgContent.replace('<svg', `<svg width="${svgWidth}" height="${svgHeight}"`);
          console.log('📐 [ChatMessageRenderer] Injected dimensions:', { svgWidth, svgHeight });
        }
        const dataUrl = `data:image/svg+xml,${encodeURIComponent(svgContent)}`;
        console.log('✅ [ChatMessageRenderer] SVG data URL generated, length:', dataUrl.length);
        return (
          <img
            src={dataUrl}
            alt="Chart"
            style={{ maxWidth: '100%', height: 'auto', display: 'block' }}
            onLoad={() => console.log('✅ [ChatMessageRenderer] SVG img loaded successfully')}
            onError={(e) => {
              console.error('❌ [ChatMessageRenderer] SVG img failed to load:', e);
              setImageError(true);
            }}
          />
        );
      }
      console.warn('⚠️ [ChatMessageRenderer] SVG content does NOT start with <svg, falling back to img src');
      return (
        <img
          src={content}
          alt="Chart"
          style={{ maxWidth: '100%', height: 'auto', display: 'block' }}
          onError={() => setImageError(true)}
        />
      );

    case 'image':
      if (imageError) {
        return <span style={{ fontSize: '13px', color: '#888' }}>Image could not be loaded.</span>;
      }
      return (
        <img
          src={content}
          alt="HydroBot response"
          style={{ maxWidth: '100%', height: 'auto', display: 'block', borderRadius: '4px' }}
          onError={() => setImageError(true)}
        />
      );

    case 'link':
      let linkUrl: URL | null = null;
      let displayText = content;
      
      try { 
        linkUrl = new URL(content);
        displayText = url || content; // Usar url si está disponible, sino el content
      } catch { 
        // Si no es una URL válida, usar el content como texto
        displayText = url || content;
      }

      return (
        <a
          href={content}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 10px',
            border: '1px solid #e0e0e0',
            borderRadius: '6px',
            textDecoration: 'none',
            color: '#078CB3',
            fontSize: '13px',
            backgroundColor: '#f9f9f9',
            transition: 'background-color 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f0f8ff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#f9f9f9';
          }}
        >
          <span style={{ fontSize: '16px' }}>🔗</span>
          <span style={{ wordBreak: 'break-all' }}>
            {displayText.length > 50 ? displayText.substring(0, 50) + '...' : displayText}
          </span>
          <span style={{ fontSize: '11px', color: '#666', marginLeft: 'auto' }}>↗</span>
        </a>
      );

    case 'video': {
      let url: URL | null = null;
      try { url = new URL(content); } catch { /* invalid url, fall through to link card */ }

      return (
        <a
          href={content}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 10px',
            border: '1px solid #e0e0e0',
            borderRadius: '6px',
            textDecoration: 'none',
            color: '#078CB3',
            fontSize: '13px',
            backgroundColor: '#f9f9f9',
          }}
        >
          <span style={{ fontSize: '18px' }}>▶</span>
          <span style={{ wordBreak: 'break-all' }}>{url?.hostname ?? content}</span>
        </a>
      );
    }

    case 'text':
    default:
      return <span style={{ fontSize: '13px', whiteSpace: 'pre-wrap' }}>{content}</span>;
  }
};

export default ChatMessageRenderer;
